import { memo, useCallback } from 'react';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import ReportSubSection from '@components/ReportSubSection';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { useTracking } from 'react-tracking';
import { Button, Grid, Box, useTheme, Text } from '@simplywallst/ui-core';
import { RouterLink } from '@redux-first-router-link';
import useInvestingIdea from '@/pages/CompanyReport/hooks/useInvestingIdea';
import type { GridViewOptions } from '@/pages/CompanyReport/constants/constants';
import { getIdeanameBySection } from '../../utils';
import { CompanyTile, CompanyTileSkeleton } from '@/components/CompanyTile';
import type { Company } from '@/components/TileList';
import useDividendInvestingIdea from '@/pages/CompanyReport/hooks/useDividendInvestingIdea';
import { useTranslation } from '@/hooks/useTranslation';
import { capitalize } from 'lodash';
import Carousel from '@/components/Carousel';
import Media from 'react-media';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import type { NextStepSections } from '../../NextStep';
import { useLocale } from '@/hooks/useLocale';
import { toLocaleUrl } from '@/pages/CompanyReport/utils/toLocaleUrl';
import { StyledEmbla } from '@/components/Carousel/styled';

const MemoTile = memo(CompanyTile);

interface SimilarCompaniesProps {
  title: string;
  section: NextStepSections;
  market: PrimaryCountryISOElseGlobal;
  industry: string;
  companies: Company[];
  gridViewOptionId: GridViewOptions;
  isLoading?: boolean;
  isErrored?: boolean;
  companyId: string;
}

export const SimilarCompanies = ({
  section,
  title,
  companies,
  market,
  industry,
  gridViewOptionId,
  isLoading,
  isErrored,
  companyId,
}: SimilarCompaniesProps) => {
  const isPristine = useVisibleSectionsIsPristine();

  const ideaName = getIdeanameBySection(section);

  const generalPayload = useInvestingIdea(
    ideaName,
    gridViewOptionId,
    market,
    industry
  );
  const dividendPayload = useDividendInvestingIdea(market.toLowerCase());

  const theme = useTheme();

  const { isMobile, isTablet } = useUserAgent();

  const payload = section === 'dividend' ? dividendPayload : generalPayload;

  const { Track, trackEvent } = useTracking({
    type: 'track',
  });

  const isSuccess = !isLoading && !isErrored;

  const trackNavigateToScreener = useCallback(
    (trigger: string) => {
      trackEvent({
        action: 'click',
        subject: `${section}_next_steps${trigger}`,
        modifier: 'grid',
        companyId,
      });
    },
    [companyId, section, trackEvent]
  );

  const t = useTranslation();

  const locale = useLocale();

  return (
    <Track>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title={title}
        body={
          <>
            <Box marginBottom="x2">
              <Media
                query={{ maxWidth: theme.mdMaxRaw }}
                defaultMatches={isMobile || isTablet}
              >
                {(matches) => {
                  return (
                    <StyledEmbla className="mt-x2">
                      <Carousel
                        align="center"
                        containScroll="trimSnaps"
                        showArrows={matches}
                        showGradient={matches}
                      >
                        <Grid
                          gap="16px"
                          templateColumns="repeat(3, 70%)"
                          templateColumnsSm="repeat(3, 50%)"
                          templateColumnsMd="repeat(3, 1fr)"
                          width="100%"
                        >
                          {isLoading && (
                            <>
                              <CompanyTileSkeleton />
                              <CompanyTileSkeleton />
                              <CompanyTileSkeleton />
                            </>
                          )}
                          {isSuccess &&
                            companies.map((company, index) => {
                              return (
                                <MemoTile
                                  index={index}
                                  key={`${company.companyId}-stock-tile`}
                                  companyName={company.shortName}
                                  companyId={company.companyId}
                                  canonicalUrl={toLocaleUrl(
                                    locale,
                                    company.canonicalUrl
                                  )}
                                  uniqueSymbol={company.uniqueSymbol}
                                  description={company.shortDescription}
                                  website={company.website}
                                  marketCap={company.marketCapFormatted}
                                  return7D={company.return7D}
                                  return7DFormatted={company.return7DFormatted}
                                  return1Y={company.return1YrAbs}
                                  return1YFormatted={
                                    company.return1YrAbsFormatted
                                  }
                                  tickerSymbol={company.tickerSymbol}
                                  sharePrice={company.sharePriceFormatted}
                                  snowflakePoints={company.snowflakePoints}
                                  industry={company.industryLabel}
                                  peRatio={company.peFormatted}
                                  growthEstimate={
                                    company.growthEstimateFormatted
                                  }
                                  analystPriceTarget={
                                    company.analystsPriceTargetFormatted
                                  }
                                  isRecentSearchEntry={
                                    company.isRecentSearchEntry
                                  }
                                  hasLogo={company.hasLogo}
                                />
                              );
                            })}
                        </Grid>
                      </Carousel>
                    </StyledEmbla>
                  );
                }}
              </Media>
            </Box>
            {payload && payload.linkToInvestingIdea && (
              <RouterLink
                to={payload.linkToInvestingIdea}
                onClick={() => trackNavigateToScreener('')}
                noUnderline
              >
                <Button styleType="loudLarge" display="block">
                  <Text typography="small" color="brand01" isTruncated>
                    {t(`page.companyReport.components.nextStep.${section}`, {
                      industry: capitalize(industry),
                      market: market.toUpperCase(),
                    })}
                  </Text>
                </Button>
              </RouterLink>
            )}
          </>
        }
      />
    </Track>
  );
};
